/*
 * The Show Day screen's state, shared with the classic panel.
 *
 * `runOfShow` decides what "next" means, and it does so from the **cue statuses**, not from the
 * clock. A cue carries a time of day and no date, so the client cannot tell whether 18:20 is
 * tonight, yesterday or next month — and a run of show that reordered itself at midnight, or
 * declared every cue late because the show is next week, would be worse than one that follows what
 * the stage manager has actually marked off. The first cue not yet executed is next. That is a fact
 * about the board; the clock is not consulted.
 */
import { useCallback, useEffect, useState } from 'react';
import { adminApi, ApiError, opsApi } from '../../../api';
import type {
  AdminStatsDto, OpsCueDto, OpsCueInput, OpsCueStatus, OpsDepartmentDto, OpsDockDto,
  OpsDockInput, OpsDockStatus, OpsDockSummaryDto,
  OpsIncidentDto, OpsIncidentInput, OpsIncidentSeverity, OpsStaffSummaryDto,
} from '../../../api';

/** Where a cue sits in the run, derived rather than stored. */
export type CuePosition = 'done' | 'next' | 'later';

export interface RunCue {
  cue: OpsCueDto;
  position: CuePosition;
  /** The owner as the record names one, or null. `importedOwnerName` is the import's leftover. */
  owner: string | null;
}

/** `HH:MM` sorts lexically, so no parsing is needed and a malformed value sinks to the end. */
const byTime = (a: OpsCueDto, b: OpsCueDto) => a.time.localeCompare(b.time);

export function runOfShow(cues: readonly OpsCueDto[]): RunCue[] {
  const ordered = [...cues].sort(byTime);
  let seenNext = false;
  return ordered.map((cue) => {
    let position: CuePosition;
    if (cue.status === 'executed') {
      position = 'done';
    } else if (!seenNext) {
      position = 'next';
      seenNext = true;
    } else {
      position = 'later';
    }
    return {
      cue,
      position,
      owner: cue.ownerName || cue.departmentName || cue.importedOwnerName || null,
    };
  });
}

export interface ShowStatus {
  /** `delayed` the moment any cue is marked so — one late cue is the whole show running late. */
  state: 'not_started' | 'on_track' | 'delayed' | 'finished';
  delayedCount: number;
  /**
   * TICK-142 — the worst single delay on the board, in minutes.
   *
   * The worst rather than the sum: delays on a running order overlap, so adding them would claim a
   * lateness nobody experienced. What an operator needs is how far behind the worst cue is.
   */
  worstDelayMinutes: number;
  doneCount: number;
  total: number;
  next: RunCue | null;
}

export function showStatus(run: readonly RunCue[]): ShowStatus {
  const total = run.length;
  const doneCount = run.filter((r) => r.position === 'done').length;
  const delayedCount = run.filter((r) => r.cue.status === 'delayed').length;
  const worstDelayMinutes = run.reduce((worst, r) => Math.max(worst, r.cue.delayMinutes ?? 0), 0);
  const next = run.find((r) => r.position === 'next') ?? null;

  const state: ShowStatus['state'] = total === 0 ? 'not_started'
    : doneCount === total ? 'finished'
      : delayedCount > 0 ? 'delayed'
        : doneCount === 0 ? 'not_started' : 'on_track';

  return { state, delayedCount, worstDelayMinutes, doneCount, total, next };
}

/** Load-in rounds that have not arrived. Real, and the frame leaves them out entirely. */
export function pendingDocks(docks: readonly OpsDockSummaryDto[]): OpsDockSummaryDto[] {
  return docks.filter((d) => d.status === 'scheduled');
}

/** Open first, worst first — the server already sorts; this is what the tiles count. */
export interface IncidentSummary {
  open: number;
  critical: number;
  /** Decisions recorded across every incident. The morning-after record, counted. */
  decisions: number;
}

export function summarizeIncidents(incidents: readonly OpsIncidentDto[]): IncidentSummary {
  const live = incidents.filter((i) => i.status !== 'resolved');
  return {
    open: live.length,
    critical: live.filter((i) => i.severity === 'critical').length,
    decisions: incidents.reduce((n, i) => n + i.decisions.length, 0),
  };
}

export interface ShowDayState {
  cues: OpsCueDto[];
  run: RunCue[];
  status: ShowStatus;
  incidents: OpsIncidentDto[];
  incidentSummary: IncidentSummary;
  docks: OpsDockSummaryDto[];
  departments: OpsDepartmentDto[];
  crew: OpsStaffSummaryDto[];
  stats: AdminStatsDto | null;
  loading: boolean;
  loadError: string;
  error: string;
  busyId: string | null;
  setCueStatus: (cue: OpsCueDto, status: OpsCueStatus) => Promise<void>;
  setCueDelay: (cue: OpsCueDto, minutes: number) => Promise<void>;
  saveCue: (input: OpsCueInput, existing: OpsCueDto | null) => Promise<boolean>;
  removeCue: (cue: OpsCueDto) => Promise<void>;
  openIncident: (input: OpsIncidentInput) => Promise<boolean>;
  setIncidentStatus: (incident: OpsIncidentDto, status: OpsIncidentDto['status']) => Promise<void>;
  setIncidentSeverity: (incident: OpsIncidentDto, severity: OpsIncidentSeverity) => Promise<void>;
  recordDecision: (incident: OpsIncidentDto, decision: string, rationale: string) => Promise<boolean>;
  /*
   * The load-in board. The endpoints have existed since TICK-124 and the classic panel used all
   * four; the rebuilt screen only listed them, so half of "คิวและลงของ" was a read-only report of
   * a board nobody could write to.
   */
  saveDock: (input: OpsDockInput, existing: OpsDockSummaryDto | null) => Promise<boolean>;
  setDockStatus: (dock: OpsDockSummaryDto, status: OpsDockStatus) => Promise<void>;
  removeDock: (dock: OpsDockSummaryDto) => Promise<void>;
  /**
   * The driver's phone, which the board masks. One record, on request — the same shape People
   * uses for contact details, and for the same reason: a number belongs to a person.
   */
  revealDock: (id: string) => Promise<void>;
  revealedDock: OpsDockDto | null;
  clearRevealedDock: () => void;
  reload: () => Promise<void>;
}

export function useShowDay(token: string, eventSlug: string | undefined): ShowDayState {
  const [cues, setCues] = useState<OpsCueDto[] | null>(null);
  const [incidents, setIncidents] = useState<OpsIncidentDto[]>([]);
  const [docks, setDocks] = useState<OpsDockSummaryDto[]>([]);
  const [revealedDock, setRevealedDock] = useState<OpsDockDto | null>(null);
  const [departments, setDepartments] = useState<OpsDepartmentDto[]>([]);
  const [crew, setCrew] = useState<OpsStaffSummaryDto[]>([]);
  const [stats, setStats] = useState<AdminStatsDto | null>(null);
  const [loadError, setLoadError] = useState('');
  const [error, setError] = useState('');
  const [busyId, setBusyId] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      const [c, i, d, dept, s] = await Promise.all([
        opsApi.listCues(token, eventSlug),
        opsApi.listIncidents(token, eventSlug),
        opsApi.listDocks(token, eventSlug),
        opsApi.listDepartments(token, eventSlug),
        opsApi.listStaff(token, eventSlug),
      ]);
      setCues(c);
      setIncidents(i);
      setDocks(d);
      setDepartments(dept);
      setCrew(s);
      setLoadError('');
    } catch (e) {
      setLoadError(e instanceof ApiError ? e.message : 'โหลดข้อมูลวันแสดงไม่สำเร็จ');
      return;
    }
    // Attendance is a second read and never blocks the board: the run of show is the urgent half.
    try {
      setStats(await adminApi.getStats(token, eventSlug));
    } catch {
      setStats(null);
    }
  }, [token, eventSlug]);

  useEffect(() => { void load(); }, [load]);

  const run = useCallback(async (id: string | null, fn: () => Promise<unknown>, fallback: string) => {
    setBusyId(id);
    setError('');
    try {
      await fn();
      await load();
      return true;
    } catch (e) {
      setError(e instanceof ApiError ? e.message : fallback);
      return false;
    } finally {
      setBusyId(null);
    }
  }, [load]);

  return {
    cues: cues ?? [],
    run: runOfShow(cues ?? []),
    status: showStatus(runOfShow(cues ?? [])),
    incidents,
    incidentSummary: summarizeIncidents(incidents),
    docks, departments, crew, stats,
    loading: cues === null && !loadError,
    loadError, error, busyId,
    setCueStatus: async (cue, status) => {
      await run(cue.id, () => opsApi.updateCue(token, cue.id, { status, version: cue.version }, eventSlug), 'เปลี่ยนสถานะคิวไม่สำเร็จ');
    },
    setCueDelay: async (cue, minutes) => {
      /*
       * Marking a delay marks the cue delayed. Recording "8 minutes late" against a cue still
       * showing `pending` would be a board that contradicts itself — and the reverse, clearing the
       * minutes, is never done here: how late a cue ran stays true after it runs.
       */
      await run(
        cue.id,
        () => opsApi.updateCue(
          token, cue.id,
          { delayMinutes: Math.max(0, minutes), status: minutes > 0 && cue.status === 'pending' ? 'delayed' : cue.status, version: cue.version },
          eventSlug,
        ),
        'บันทึกเวลาดีเลย์ไม่สำเร็จ',
      );
    },
    saveCue: (input, existing) => run(
      existing?.id ?? 'new',
      () => (existing
        ? opsApi.updateCue(token, existing.id, { ...input, version: existing.version }, eventSlug)
        : opsApi.createCue(token, input, eventSlug)),
      'บันทึกคิวไม่สำเร็จ',
    ),
    removeCue: async (cue) => {
      await run(cue.id, () => opsApi.deleteCue(token, cue.id, cue.version, eventSlug), 'ลบคิวไม่สำเร็จ');
    },
    saveDock: (input, existing) => run(
      existing?.id ?? 'new-dock',
      () => {
        if (!existing) return opsApi.createDock(token, input, eventSlug);
        /*
         * An empty phone box on an edit means "leave it alone", not "erase it". The board only
         * ever holds the masked number, so the form cannot prefill the real one — and sending the
         * empty string would quietly delete a number that was never on screen to begin with.
         * `updateDock` takes a partial, so the field is simply left out.
         */
        const { driverPhone, ...rest } = input;
        return opsApi.updateDock(
          token,
          existing.id,
          driverPhone.trim()
            ? { ...rest, driverPhone, version: existing.version }
            : { ...rest, version: existing.version },
          eventSlug,
        );
      },
      'บันทึกรอบลงของไม่สำเร็จ',
    ),
    setDockStatus: async (dock, status) => {
      /*
       * Marking a truck arrived stamps the time, because a board that says "arrived" without
       * saying when cannot answer the only question asked of it afterwards. Moving back to
       * scheduled clears it: the arrival did not happen.
       */
      await run(
        dock.id,
        () => opsApi.updateDock(
          token, dock.id,
          {
            status,
            arrivedTime: status === 'scheduled'
              ? null
              : dock.arrivedTime || new Date().toTimeString().slice(0, 5),
            version: dock.version,
          },
          eventSlug,
        ),
        'เปลี่ยนสถานะรอบลงของไม่สำเร็จ',
      );
    },
    removeDock: async (dock) => {
      await run(dock.id, () => opsApi.deleteDock(token, dock.id, dock.version, eventSlug), 'ลบรอบลงของไม่สำเร็จ');
    },
    revealDock: async (id) => {
      setError('');
      try {
        setRevealedDock(await opsApi.getDock(token, id, eventSlug));
      } catch (e) {
        setError(e instanceof ApiError ? e.message : 'ดูเบอร์คนขับไม่สำเร็จ');
      }
    },
    revealedDock,
    clearRevealedDock: () => setRevealedDock(null),
    openIncident: (input) => run('new-incident', () => opsApi.createIncident(token, input, eventSlug), 'บันทึกเหตุการณ์ไม่สำเร็จ'),
    setIncidentStatus: async (incident, status) => {
      await run(
        incident.id,
        () => opsApi.updateIncident(token, incident.id, { status, version: incident.version }, eventSlug),
        'เปลี่ยนสถานะเหตุการณ์ไม่สำเร็จ',
      );
    },
    setIncidentSeverity: async (incident, severity) => {
      await run(
        incident.id,
        () => opsApi.updateIncident(token, incident.id, { severity, version: incident.version }, eventSlug),
        'เปลี่ยนระดับความรุนแรงไม่สำเร็จ',
      );
    },
    recordDecision: (incident, decision, rationale) => run(
      incident.id,
      () => opsApi.recordIncidentDecision(token, incident.id, { decision, rationale }, eventSlug),
      'บันทึกการตัดสินใจไม่สำเร็จ',
    ),
    reload: load,
  };
}
